import React, { useState, useMemo, useEffect } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal, Card, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import distributorService from '../../../../services/DistributorService';

const CollectRetailerOutstanding = () => {
  const navigate = useNavigate();
  const [retailers, setRetailers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [collectionData, setCollectionData] = useState({
    amount: '',
    paymentMethod: 'Cash',
    referenceNumber: '',
    notes: '',
    collectedBy: '',
    collectionDate: new Date().toISOString().split('T')[0],
    remark: '',
    utr: '',
    transactionType: 'DEBIT'
  });
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedRetailerForUpdate, setSelectedRetailerForUpdate] = useState(null);
  const [updateData, setUpdateData] = useState({
    operator: '',
    fos: '',
    creditLimit: ''
  });
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [beatsList, setBeats] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRetailersWithOutstanding();
    }, searchQuery ? 500 : 0); // Debounce search

    return () => clearTimeout(timer);
  }, [currentPage, rowsPerPage, searchQuery, columnFilters]);

  useEffect(() => {
    fetchBeats();
    fetchOutstandingSummary();
  }, []);

  const fetchBeats = async () => {
    try {
      const response = await distributorService.getBeats();
      
      console.log('Beats List Response:', response);
      
      if (response.success && response.data) {
        console.log('Beats List Data:', response.data.beats);
        setBeats(response.data.beats || []);
      } else {
        console.error('Beats List Error:', response.error);
        setBeats([]);
      }
    } catch (error) {
      console.error('Error fetching beats list:', error);
      setBeats([]);
    }
  };

  const fetchRetailersWithOutstanding = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        search: searchQuery,
        beatName: columnFilters.beatName,
        operator: columnFilters.operator
      };

      const response = await distributorService.getRetailerProfiles(params);
      
      console.log('Service Response:', response);
      
      if (response.success && response.data) {
        const transformedData = response.data.results.map((retailer, index) => ({
          id: retailer.id,
          customerId: retailer.customer_id,
          storeName: retailer.user_shop_name,
          retailerId: retailer.retailer_id,
          ownerName: retailer.user_name,
          mobile: retailer.user_mobile,
          // Beat Information
          beatId: retailer.beat_id,
          beatName: retailer.beat_name,
          // Distributor Information
          distributorMobile: retailer.distributor_mobile,
          // Other details
          totalOutstanding: parseFloat(retailer.outstanding_amount),
          creditLimit: parseFloat(retailer.credit_limit),
          operator: retailer.operator,
          autoRefillStatus: retailer.auto_refill_status,
          city: retailer.user_city,
          district: retailer.user_district,
          paymentHistory: [] // Would need separate API call for payment history
        }));
        // Remove the filter to show all retailers for testing
        // })).filter(retailer => retailer.totalOutstanding > 0);

        console.log('Transformed Data:', transformedData);
        setRetailers(transformedData);
        setTotalCount(response.data.count);

        // Extract unique beats list from retailer data as fallback
        const uniqueBeats = response.data.results.reduce((acc, retailer) => {
          if (retailer.beat_name) {
            const existingBeat = acc.find(beat => beat.name === retailer.beat_name);
            if (!existingBeat) {
              acc.push({
                id: retailer.beat_id,
                name: retailer.beat_name
              });
            }
          }
          return acc;
        }, []);
        
        // Update beats list if we don't have any from the API
        if (beatsList.length === 0) {
          console.log('Using beats from retailer data:', uniqueBeats);
          setBeats(uniqueBeats);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch retailer outstanding data');
      console.error('Error fetching retailers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    // API already handles pagination and filtering, so return as is
    return retailers;
  }, [retailers]);

  const paginatedData = useMemo(() => {
    // Since API already handles pagination, return filtered data as is
    return filteredData;
  }, [filteredData]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const totalOutstandingAmount = filteredData.reduce((sum, retailer) => sum + retailer.totalOutstanding, 0);

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
    setCurrentPage(1);
  };


  const exportToExcel = async () => {
    setLoading(true);
    try {
      const params = {
        
      };

      const response = await distributorService.exportOutstandingData(params);
      
      if (response.success) {
        // Create blob and download file
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `retailer_outstanding_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Outstanding data exported successfully');
      } else {
        toast.error('Failed to export outstanding data');
      }
    } catch (error) {
      toast.error('Failed to export outstanding data');
      console.error('Error exporting outstanding data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCollect = (retailer) => {
    setSelectedRetailer(retailer);
    setCollectionData({
      amount: '',
      paymentMethod: 'Cash',
      referenceNumber: '',
      notes: '',
      collectedBy: '',
      collectionDate: new Date().toISOString().split('T')[0],
      transactionDate: new Date().toISOString().split('T')[0],
      remark: '',
      utr: '',
      transactionType: 'DEBIT',
      paymentFor: 'ETOPUP'
    });
    setShowCollectionModal(true);
  };

  const handleCollectionSubmit = async (e) => {
    e.preventDefault();
    
    if (!collectionData.amount || isNaN(collectionData.amount)) {
      toast.error('Please enter a valid amount');
      return;
    }

    const collectionAmount = parseFloat(collectionData.amount);
    // if (collectionAmount > selectedRetailer.totalOutstanding) {
    //   toast.error('Collection amount cannot exceed outstanding amount');
    //   return;
    // }

    setLoading(true);
    try {
      // Generate order ID with current timestamp
      const generateOrderId = () => {
        const now = new Date();
        const timestamp = now.toISOString().replace(/[-:]/g, '').replace('T', '_').split('.')[0];
        const random = Math.floor(Math.random() * 10000);
        return `${timestamp}_${random}`;
      };

      // Map payment method to payment mode
      const getPaymentMode = (method) => {
        switch (method.toLowerCase()) {
          case 'cash': return 'CASH';
          case 'upi': return 'UPI';
          case 'deposit': return 'DEPOSIT';
          case 'bank transfer':
          case 'cheque':
          case 'dd':
          default: return 'CREDIT';
        }
      };

       const getTransactionType = (method) => {
        // console.log('collectionData.transactionType:', collectionData.transactionType);
        switch (method.toLowerCase()) {
          case 'credit': return 'CREDIT';
          case 'debit': return 'DEBIT';
          default: return 'DEBIT';
        }
      };

      const transactionData = {
        retailerProfileId: selectedRetailer.id,
        amount: collectionAmount,
        orderId: generateOrderId(),
        transactionType: getTransactionType(collectionData.transactionType), // Payment collection is always DEBIT
        txnType: collectionData.paymentFor, // Payment For value
        paymentMode: getPaymentMode(collectionData.paymentMethod),
        transactionDate: collectionData.transactionDate,
        remark: collectionData.remark,
        utr: collectionData.utr,
      };

      const response = await distributorService.recordOutstandingTransaction(transactionData);
      
      if (response.success) {
        const { transaction, balance_summary } = response.data;
        
        // Update local state with new outstanding amount from API response
        setRetailers(prev => prev.map(r => 
          r.id === selectedRetailer.id ? { 
            ...r, 
            totalOutstanding: balance_summary.current_outstanding,
            paymentHistory: [
              { 
                date: transaction.transaction_date, 
                amount: transaction.amount, 
                method: transaction.payment_mode,
                reference: transaction.order_id,
                collectedBy: transaction.collected_by_mobile,
                transactionId: transaction.id,
                status: transaction.status
              },
              ...r.paymentHistory
            ]
          } : r
        ));
        
        toast.success(response.message || `₹${collectionAmount.toLocaleString()} collected successfully`);
        setShowCollectionModal(false);
        setSelectedRetailer(null);
        
        // Refresh the data to get updated outstanding amounts
        fetchRetailersWithOutstanding();
        fetchOutstandingSummary();
      } else {
        toast.error(response.error || 'Failed to record payment collection');
      }
    } catch (error) {
      toast.error('Failed to record payment collection');
      console.error('Error recording collection:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCollectionData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdate = (retailer) => {
    setSelectedRetailerForUpdate(retailer);
    setUpdateData({
      operator: retailer.operator,
      beat: retailer.beatId,
      creditLimit: retailer.creditLimit
    });
    setShowUpdateModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    
    if (!updateData.operator || !updateData.beat) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await distributorService.updateRetailerProfile(
        selectedRetailerForUpdate.id, 
        updateData
      );
      
      if (response.success) {
        toast.success(response.message || 'Retailer profile updated successfully');
        setShowUpdateModal(false);
        setSelectedRetailerForUpdate(null);
        
        // Refresh the data
        fetchRetailersWithOutstanding();
      } else {
        toast.error(response.error || 'Failed to update retailer profile');
      }
    } catch (error) {
      toast.error('Failed to update retailer profile');
      console.error('Error updating retailer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInputChange = (field, value) => {
    setUpdateData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleViewHistory = (retailer) => {
    navigate(`/distributor/ledger/retailer/${retailer.retailerId}/${retailer.id}`, {
      state: { retailerData: retailer }
    });
  };

  const fetchOutstandingSummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await distributorService.getOutstandingSummary();
      
      if (response.success) {
        setSummaryData(response.data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };


  const getAmountColor = (amount) => {
    if (amount > 30000) return 'text-danger fw-bold';
    if (amount > 15000) return 'text-warning fw-bold';
    return 'text-info fw-bold';
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-hand-holding-usd me-2"></i>
          Collect Retailer Outstanding
        </h5>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={fetchRetailersWithOutstanding} disabled={loading}>
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </Button>
          <Button variant="success" onClick={exportToExcel} disabled={loading}>
            <i className="fas fa-file-excel me-2"></i>
            {loading ? 'Exporting...' : 'Export Excel'}
          </Button>
        </div>
      </div>


      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search retailers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={columnFilters.beatName || ''}
            onChange={(e) => handleColumnFilterChange('beatName', e.target.value)}
          >
            <option value="">All Beats</option>
            {console.log('Rendering Beats List:', beatsList)}
            {beatsList && beatsList.length > 0 ? (
              beatsList.map((beat) => (
                <option key={beat.id} value={beat.name}>
                  {beat.name}
                </option>
              ))
            ) : (
              <option disabled>No beats available</option>
            )}
          </Form.Select>
        </Col>
        <Col md={3}>
          {summaryLoading ? (
            <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ height: '38px' }}>
              <Spinner animation="border" size="sm" />
              <small className="ms-2">Loading...</small>
            </div>
          ) : summaryData ? (
            <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ height: '38px' }}>
              <span className="text-muted small">Total Outstanding: <span className="text-danger fw-bold">₹{Math.floor(summaryData?.total_outstanding || 0).toLocaleString()}</span></span>
            </div>
          ) : (
            <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ height: '38px' }}>
              <small className="text-muted">No data</small>
            </div>
          )}
        </Col>
        <Col md={2}>
          {summaryData && (
            <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ height: '38px' }}>
              <span className="text-muted small">Total Retailers: <span className="text-primary fw-bold">{summaryData?.total_retailers || 0}</span></span>
            </div>
          )}
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table striped bordered hover className="modern-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th>Outstanding Amount</th>
              <th>Credit Limit</th>
              <th>Store Name</th>
              <th>Owner Name</th>
              <th>Customer ID</th>
              <th>Mobile</th>
              <th>City</th>
              <th>Beat</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-4 text-muted">
                  No retailers with outstanding amounts found
                </td>
              </tr>
            ) : (
              paginatedData.map((retailer) => (
              <tr key={retailer.retailerId}>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleCollect(retailer)}
                      title="Collect Payment"
                    >
                      <i className="fas fa-money-bill-wave"></i>
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleUpdate(retailer)}
                      title="Update Profile"
                    >
                      <i className="fas fa-edit"></i>
                    </Button>
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleViewHistory(retailer)}
                      title="View History"
                    >
                      <i className="fas fa-history"></i>
                    </Button>
                  </div>
                </td>
                <td>
                  <div className={getAmountColor(retailer.totalOutstanding || 0)}>
                    ₹{(retailer.totalOutstanding || 0).toLocaleString()}
                  </div>
                </td>
                <td>
                  ₹{(retailer.creditLimit || 0).toLocaleString()}
                </td>
                <td>
                  <strong>{retailer.storeName}</strong>
                </td>
                <td>
                  {retailer.ownerName}
                </td>
                <td>
                  {retailer.customerId}
                </td>
                <td>
                  {retailer.mobile}
                </td>
                <td>
                  {retailer.city}, {retailer.district}
                </td>
                <td>
                  <div>
                    <div><strong>{retailer.beatName || 'Not Assigned'}</strong></div>
                  </div>
                </td>
              </tr>
            ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {retailers.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1} 
          />
          {(() => {
            const pages = [];
            const maxVisiblePages = 5;
            
            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
            
            if (endPage - startPage < maxVisiblePages - 1) {
              startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }
            
            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <Pagination.Item
                  key={i}
                  active={i === currentPage}
                  onClick={() => setCurrentPage(i)}
                >
                  {i}
                </Pagination.Item>
              );
            }
            
            return pages;
          })()}
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages} 
          />
        </Pagination>
      </div>

      {/* Collection Modal */}
      <Modal show={showCollectionModal} onHide={() => setShowCollectionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title><span> Outstanding : ₹{selectedRetailer?.totalOutstanding?.toLocaleString() || '0'} </span></Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCollectionSubmit}>
          <Modal.Body>
            {selectedRetailer && (
              <div>
                <div className="mb-3">
                  <strong>Store:</strong> {selectedRetailer.storeName}
                </div>
                
                
                
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Collection Amount<span style={{color: 'red'}}>*</span></Form.Label>
                      <InputGroup>
                        <InputGroup.Text>₹</InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={collectionData.amount}
                          onChange={(e) => handleInputChange('amount', e.target.value)}
                          placeholder="Enter amount"
                          
                          required
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Maximum: ₹{selectedRetailer?.totalOutstanding?.toLocaleString() || '0'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Payment/Credit For<span style={{color: 'red'}}>*</span></Form.Label>
                      <Form.Select
                        value={collectionData.paymentFor}
                        onChange={(e) => handleInputChange('paymentFor', e.target.value)}
                        required
                      >
                        <option value="ETOPUP">E-Topup</option>
                        <option value="HANDSET">Handset</option>
                        <option value="SIM_CARD">SIM Card</option>
                        <option value="GIFT">Gift</option>
                        <option value="OTHER">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Payment Method<span style={{color: 'red'}}>*</span></Form.Label>
                      <Form.Select
                        value={collectionData.paymentMethod}
                        onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                        required
                      >
                        <option value="Cash">Cash</option>
                        <option value="UPI">UPI</option>
                        <option value="CREDIT">Credit</option>
                        <option value="Deposit"> Bank Deposit</option>
                        
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Transaction Date<span style={{color: 'red'}}>*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={collectionData.transactionDate}
                        onChange={(e) => handleInputChange('transactionDate', e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Transaction Type<span style={{color: 'red'}}>*</span></Form.Label>
                      <Form.Select
                        value={collectionData.transactionType}
                        onChange={(e) => handleInputChange('transactionType', e.target.value)}
                        required
                      >
                        <option value="DEBIT">DEBIT(Minus)</option>
                        <option value="CREDIT">CREDIT(Plus)</option>
                        
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {['UPI', 'Deposit'].includes(collectionData.paymentMethod) && (
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label>
                        {collectionData.paymentMethod === 'UPI'
                          ? 'UTR Number '
                          : 'Receipt Number '}}<span style={{color: 'red'}}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={collectionData.utr}
                        onChange={(e) => handleInputChange('utr', e.target.value)}
                        placeholder="Enter reference number"
                        required
                      />
                    </Form.Group>
                  </Col>
                )}

                  
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label>Remark</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={collectionData.remark}
                        onChange={(e) => handleInputChange('remark', e.target.value)}
                        placeholder="Enter any remarks or notes"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCollectionModal(false)}>
              Cancel
            </Button>
            <Button variant="success" type="submit">
              <i className="fas fa-check me-2"></i>
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Update Modal */}
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Update Retailer Profile</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateSubmit}>
          <Modal.Body>
            {selectedRetailerForUpdate && (
              <div>
                <div className="mb-3">
                  <strong>Store:</strong> {selectedRetailerForUpdate.storeName}
                </div>
                
                <div className="mb-4">
                  <strong>Mobile:</strong> {selectedRetailerForUpdate.mobile}
                </div>
                
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Operator <span style={{color: 'red'}}>*</span></Form.Label>
                      <Form.Select
                        value={updateData.operator}
                        onChange={(e) => handleUpdateInputChange('operator', e.target.value)}
                        required
                      >
                        <option value="">Select Operator</option>
                        <option value="JIO">JIO</option>
                        <option value="AIRTEL">AIRTEL</option>
                        <option value="VODAFONE">VODAFONE</option>
                        <option value="BSNL">BSNL</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Beat <span style={{color: 'red'}}>*</span></Form.Label>
                      <Form.Select
                        value={updateData.beat}
                        onChange={(e) => handleUpdateInputChange('beat', e.target.value)}
                        required
                      >
                        <option value="">Select Beat</option>
                        {beatsList.map((beat) => (
                          <option key={beat.id} value={beat.id}>
                            {beat.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label>Credit Limit <span style={{color: 'red'}}>*</span></Form.Label>
                      <InputGroup>
                        <InputGroup.Text>₹</InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={updateData.creditLimit}
                          onChange={(e) => handleUpdateInputChange('creditLimit', e.target.value)}
                          placeholder="Enter credit limit"
                          required
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              <i className="fas fa-save me-2"></i>
              {loading ? 'Updating...' : 'Update Profile'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default CollectRetailerOutstanding;